import { c as _c } from "react/compiler-runtime";
import "client-only";
import { useEffect, useEffectEvent } from "react";
import { useTracking } from "@/hooks/tracking/use-tracking";
import { useCookiebotConsent } from "@/vendors/Cookiebot/_hooks/use-cookiebot-consent.hook";
import type { ConversionEvent } from "@/vendors/Kilkaya/kilkaya.types";

/**
 * Hook to track a conversion event once on component mount.
 *
 * This intentionally tracks on every mount to support scenarios where
 * users might perform multiple conversions in the same session
 * (e.g., subscribing multiple email addresses for colleagues).
 *
 * The event will NOT be tracked on rerenders, only on mount/unmount cycles.
 */
export function useConversionTracking(conversionEvent) {
  const $ = _c(7);
  const {
    emit
  } = useTracking();
  const consent = useCookiebotConsent();
  let t0;
  if ($[0] !== consent || $[1] !== conversionEvent || $[2] !== emit) {
    t0 = () => {
      if (conversionEvent === null) {
        return;
      }
      emit({
        event: "conversion",
        model: {
          consent,
          event: conversionEvent
        }
      });
    };
    $[0] = consent;
    $[1] = conversionEvent;
    $[2] = emit;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const trackConversion = useEffectEvent(t0);
  let t1;
  if ($[4] !== trackConversion) {
    t1 = () => {
      trackConversion();
    };
    $[4] = trackConversion;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
}